import { CommentsSection } from '../community/comments-section'
import { ImageFileId } from '../image/image-file-id'
import { imageUtil } from '../../common/util/image-util'
import { MessageCircle } from 'lucide-react'
import React, { useState } from 'react'
import type { GroupPost } from '../../types/groups'
import { stringUtil } from '../../util/string-util'
import { timeUtil } from '../../util/time-util'

interface PostCardProps {
  post: GroupPost
  onDelete?: (postId: string) => void
}

export const PostCard = ({ post, onDelete }: PostCardProps): React.ReactElement => {
  const [showComments, setShowComments] = useState(false)
  const displayName = stringUtil.getDisplayName(post.hustler ?? null)

  return (
    <div className="rounded-xl bg-white/5 p-4 mb-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-ivory text-sm font-semibold overflow-hidden">
          {post.hustler?.profilePhotoFileId ? (
            <ImageFileId fileId={post.hustler.profilePhotoFileId} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            stringUtil.initials(displayName ?? '')
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-ivory font-bold uppercase text-sm">{displayName}</span>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-500">{timeUtil.formatRelativeTime(post.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              {post.isPinned && <span className="text-yellow-500 text-xs font-semibold">PINNED</span>}
              {onDelete && (
                <button
                  onClick={() => onDelete(post.id)}
                  className="text-white/20 hover:text-red-400 transition-colors text-xs"
                  title="Delete post"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <p className="text-gray-300 text-sm whitespace-pre-wrap mb-1">{post.text}</p>
          {post.photoFileId && (
            <img src={imageUtil.getImageUrl(post.photoFileId)} alt="Post" className="rounded-lg max-w-full mb-3" />
          )}
          {post.workout && (
            <div className="bg-gray-800 rounded p-3 mb-3">
              <p className="text-sm text-gray-400">Shared workout:</p>
              <p className="text-ivory font-semibold">{post.workout.title ?? post.workout.name}</p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowComments(!showComments)}
            className="text-white/40 hover:text-white/60 text-sm flex items-center gap-1.5 transition-colors mt-2"
          >
            <MessageCircle className="w-4 h-4" />
            <span>
              {showComments
                ? 'Hide comments'
                : post.commentCount
                ? `${post.commentCount} ${post.commentCount === 1 ? 'comment' : 'comments'}`
                : 'Comments'}
            </span>
          </button>
        </div>
      </div>
      {showComments && (
        <div className="mt-3 pt-3 border-t border-white/[0.06] pl-6">
          <CommentsSection entityType="post" entityId={post.id} compact small />
        </div>
      )}
    </div>
  )
}
